export type FunPackControl = 'INHERIT' | 'OFF'

export type ChatControl = 'FULL' | 'ANNOUNCEMENTS_ONLY' | 'NO_DM' | 'OFF'

export type AiControlMode = 'INHERIT' | 'OFF' | 'BUDGET'

export interface AiControl {
  mode: AiControlMode
  dailyCredits?: number
}

export interface MemberControls {
  funPack?: FunPackControl
  chat?: ChatControl
  ai?: AiControl
  tiles?: string[] | null
}

export interface ControlProfile {
  id?: string
  name: string
  controls: MemberControls
  updatedAt?: unknown
  updatedBy?: string
}

export interface MemberControlsSettings {
  profileId?: string | null
  overrides?: MemberControls | null
}

export interface MemberAiDailyBudgetCounter {
  dayKey: string
  committed: number
}

export const HIDEABLE_TAB_KEYS = [
  // Shared / member / helper tabs
  'board',
  'nexus',
  'ai',
  'stock',
  'forms',
  'sync',
  'automations',
  'class',
  'field',
  'chat',
  'calendar',
  'meetings',
  'school',
  // Web-only pages
  'analysis',
  'reports',
  'ledger',
  'budget',
  'quota',
  'design',
  'upload',
  'link',
  'teaching',
  'training',
  'todos',
  'checklists',
  'focus',
  'raid',
  'clients',
  'timesheets',
] as const

export type HideableTabKey = (typeof HIDEABLE_TAB_KEYS)[number]
export type PageKey = HideableTabKey
export const HIDEABLE_TAB_KEY_SET: ReadonlySet<string> = new Set(HIDEABLE_TAB_KEYS)

export const NEVER_HIDEABLE_KEYS = [
  'team',
  'settings',
  'your-page',
  'purchase',
  'dashboard',
  'for-you',
  'tasks',
] as const

export type NeverHideableKey = (typeof NEVER_HIDEABLE_KEYS)[number]
export const NEVER_HIDEABLE_KEY_SET: ReadonlySet<string> = new Set([
  ...NEVER_HIDEABLE_KEYS,
  'your page',
  'your_page',
  'workspace_settings',
  'workspace-settings',
  'user_settings',
  'user-settings',
  'invites',
])

export function isNeverHideableKey(key: string): boolean {
  return NEVER_HIDEABLE_KEY_SET.has(key.trim().toLowerCase())
}

export interface PageSurfaceMapping {
  web?: readonly string[]
  member?: string
  helper?: string
  neverHideable?: boolean
}

export const PAGE_SURFACE_MAPPINGS: Record<string, PageSurfaceMapping> = {
  // Never-hideable homes & protected pages (V3, TB-D9, TMP8)
  dashboard: { web: ['DASHBOARD'], neverHideable: true },
  'for-you': { member: 'for-you', neverHideable: true },
  tasks: { helper: 'tasks', neverHideable: true },
  team: { web: ['INVITES'], member: 'team', neverHideable: true },
  settings: { web: ['SETTINGS', 'WORKSPACE_SETTINGS', 'USER_SETTINGS'], neverHideable: true },
  'your-page': { web: ['YOUR_PAGE'], neverHideable: true },
  purchase: { web: ['PURCHASE'], neverHideable: true },

  // Shared / member / helper pages
  board: { web: ['OVERVIEW'], member: 'board' },
  nexus: { member: 'nexus' },
  ai: { web: ['AI_MAGIC'], member: 'ai' },
  stock: { web: ['STOCK'], member: 'stock' },
  forms: { web: ['FORMS'], member: 'forms' },
  sync: { member: 'sync' },
  automations: { web: ['AUTOMATE'], member: 'automations' },
  class: { member: 'class' },
  field: { helper: 'field' },
  chat: { web: ['CHAT'], helper: 'chat' },
  calendar: { web: ['CALENDAR'], helper: 'calendar' },
  meetings: { web: ['MEETINGS'], helper: 'meetings' },
  school: { helper: 'school' },

  // Web-only pages
  analysis: { web: ['ANALYSIS'] },
  reports: { web: ['REPORTS'] },
  ledger: { web: ['SUMMARY'] },
  budget: { web: ['BUDGET'] },
  quota: { web: ['QUOTA'] },
  design: { web: ['DESIGN'] },
  upload: { web: ['UPLOAD'] },
  link: { web: ['LINK'] },
  teaching: { web: ['TEACHING'] },
  training: { web: ['TRAINING'] },
  todos: { web: ['FOR_YOU'] },
  checklists: { web: ['CHECKLISTS'] },
  focus: { web: ['FOCUS'] },
  raid: { web: ['RAID'] },
  clients: { web: ['CLIENTS'] },
  timesheets: { web: ['TIMESHEETS'] },
}

/**
 * Canonical event type to hideable page key mapping (F3).
 */
export const EVENT_TYPE_TO_PAGE_KEY: Readonly<Record<string, PageKey>> = {
  // Stock
  STOCK_LOW: 'stock',
  STOCK_EXPIRING: 'stock',
  STOCK_TRANSFER_ARRIVED: 'stock',
  STOCK_SUGGESTION_WAITING: 'stock',

  // Reports
  REPORT_READY: 'reports',
  REPORT_FAILED: 'reports',
  EDU_REPORT_CARD_PUBLISHED: 'reports',

  // Forms
  FORM_REQUESTED: 'forms',

  // Raid / FunPack
  RAID_STARTED: 'raid',
  RAID_ENRAGE_WARNING: 'raid',
  RAID_COMPLETED: 'raid',
  RAID_FAILED: 'raid',

  // AI
  AI_JOB_COMPLETE: 'ai',
  AI_JOB_FAILED: 'ai',

  // Meetings
  MEETING_INVITE: 'meetings',
  MEETING_STARTING: 'meetings',
  MEETING_RESCHEDULED: 'meetings',
  MEETING_CANCELLED: 'meetings',

  // Chat
  GROUP_CHAT_MESSAGE: 'chat',

  // Edu
  EDU_ABSENCE: 'teaching',
  EDU_LATE: 'teaching',
  EDU_LEAVE_REQUESTED: 'teaching',
  EDU_LEAVE_DECIDED: 'teaching',
  EDU_CLASS_CANCELLED: 'teaching',
  EDU_CLASS_SUBSTITUTE: 'teaching',
  EDU_TODAY_SUMMARY: 'teaching',
  EDU_BOOKING_STAFF_BOOKED: 'teaching',
  EDU_BOOKING_PROMOTED: 'teaching',
  EDU_BOOKING_LATE_CANCEL_USED: 'teaching',
  EDU_BOOKING_NO_SHOW_USED: 'teaching',
  EDU_BOOKING_BLOCKED: 'teaching',
  EDU_BOOKING_PACK_EXPIRING: 'teaching',
  EDU_BOOKING_PACK_EXPIRED: 'teaching',
  EDU_BOOKING_MEMBERSHIP_FROZEN: 'teaching',
  EDU_BOOKING_SESSION_CANCELLED: 'teaching',

  EDU_HOMEWORK_SET: 'training',
  EDU_HOMEWORK_SUBMITTED: 'training',
  EDU_HOMEWORK_RETURNED: 'training',
  EDU_HOMEWORK_APPROVED: 'training',
  EDU_HOMEWORK_DUE: 'training',

  // Clients (F4)
  CLIENT_QUOTE_DECIDED: 'clients',

  // Automations
  AUTOMATION_TRIGGERED: 'automations',
  AUTOMATION_FAILED: 'automations',
}

export function pageKeyForEventType(eventType?: string | null): PageKey | null {
  if (!eventType) return null
  const key = EVENT_TYPE_TO_PAGE_KEY[eventType.trim().toUpperCase()]
  return key ?? null
}

export function pageKeyForView(view: string): string | null {
  const normalized = view.trim().toUpperCase()
  for (const [key, mapping] of Object.entries(PAGE_SURFACE_MAPPINGS)) {
    if (mapping.web?.includes(normalized)) {
      return key
    }
  }
  return null
}

export function pageKeyForTab(tabKey: string, surface?: 'member' | 'helper'): string | null {
  const normalized = tabKey.trim().toLowerCase()
  for (const [key, mapping] of Object.entries(PAGE_SURFACE_MAPPINGS)) {
    if (surface === 'member' && mapping.member === normalized) {
      return key
    }
    if (surface === 'helper' && mapping.helper === normalized) {
      return key
    }
    if (!surface && (mapping.member === normalized || mapping.helper === normalized || key === normalized)) {
      return key
    }
  }
  return null
}

const ALLOWED_CONTROL_KEYS = new Set(['funPack', 'chat', 'ai', 'tiles'])
const ALLOWED_FUN_PACK = new Set(['INHERIT', 'OFF'])
const ALLOWED_CHAT = new Set(['FULL', 'ANNOUNCEMENTS_ONLY', 'NO_DM', 'OFF'])
const ALLOWED_AI_MODE = new Set(['INHERIT', 'OFF', 'BUDGET'])
const ALLOWED_AI_KEYS = new Set(['mode', 'dailyCredits'])

/**
 * Shared validator for MemberControls shape and values (plan §4.3, TB-D9).
 * Refuses unknown keys, invalid enum values, improper dailyCredits, and tiles outside HIDEABLE_TAB_KEYS.
 * Also refuses never-hideable keys (team, settings, your page, purchase, dashboard, for-you, tasks).
 */
export function validateMemberControls(controls: unknown): void {
  if (!controls || typeof controls !== 'object' || Array.isArray(controls)) {
    throw new Error('Controls must be an object.')
  }

  const record = controls as Record<string, unknown>

  // 1. Unknown keys
  for (const key of Object.keys(record)) {
    if (!ALLOWED_CONTROL_KEYS.has(key)) {
      throw new Error(`Unknown control key: "${key}". Allowed keys: funPack, chat, ai, tiles.`)
    }
  }

  // 2. funPack
  if (record.funPack !== undefined) {
    if (typeof record.funPack !== 'string' || !ALLOWED_FUN_PACK.has(record.funPack)) {
      throw new Error(`Invalid funPack control: "${record.funPack}". Must be INHERIT or OFF.`)
    }
  }

  // 3. chat
  if (record.chat !== undefined) {
    if (typeof record.chat !== 'string' || !ALLOWED_CHAT.has(record.chat)) {
      throw new Error(`Invalid chat control: "${record.chat}". Must be FULL, ANNOUNCEMENTS_ONLY, NO_DM, or OFF.`)
    }
  }

  // 4. ai
  if (record.ai !== undefined) {
    if (!record.ai || typeof record.ai !== 'object' || Array.isArray(record.ai)) {
      throw new Error('ai control must be an object.')
    }
    const aiRecord = record.ai as Record<string, unknown>
    for (const key of Object.keys(aiRecord)) {
      if (!ALLOWED_AI_KEYS.has(key)) {
        throw new Error(`Unknown key in ai control: "${key}". Allowed keys: mode, dailyCredits.`)
      }
    }

    if (typeof aiRecord.mode !== 'string' || !ALLOWED_AI_MODE.has(aiRecord.mode)) {
      throw new Error(`Invalid ai.mode control: "${aiRecord.mode}". Must be INHERIT, OFF, or BUDGET.`)
    }

    if (aiRecord.mode === 'BUDGET') {
      if (
        typeof aiRecord.dailyCredits !== 'number' ||
        !Number.isInteger(aiRecord.dailyCredits) ||
        aiRecord.dailyCredits < 1
      ) {
        throw new Error('ai.mode BUDGET requires an integer dailyCredits >= 1.')
      }
    } else {
      if (aiRecord.dailyCredits !== undefined) {
        throw new Error(`dailyCredits cannot be set when ai.mode is ${aiRecord.mode}. Only BUDGET permits dailyCredits.`)
      }
    }
  }

  // 5. tiles
  if (record.tiles !== undefined && record.tiles !== null) {
    if (!Array.isArray(record.tiles)) {
      throw new Error('tiles must be an array of strings.')
    }
    if (record.tiles.length > 32) {
      throw new Error(`tiles cannot contain more than 32 items (received ${record.tiles.length}).`)
    }
    for (const raw of record.tiles) {
      if (typeof raw !== 'string') {
        throw new Error('tiles must be an array of strings.')
      }
      const key = raw.trim().toLowerCase()
      if (isNeverHideableKey(key)) {
        throw new Error(`Protected page "${raw}" cannot be hidden by member controls.`)
      }
      if (!HIDEABLE_TAB_KEY_SET.has(key)) {
        throw new Error(`Tile "${raw}" is not hideable. Allowed hideable tiles: ${HIDEABLE_TAB_KEYS.join(', ')}.`)
      }
    }
  }
}

/**
 * Backward compatibility alias for tile validation.
 */
export function validateTilesNotProtected(tiles?: string[] | null): void {
  if (!tiles || !Array.isArray(tiles)) return
  validateMemberControls({ tiles })
}

/**
 * Normalizes member controls by trimming, lower-casing, and de-duplicating tiles,
 * dropping unknown or never-hideable tiles.
 */
export function normalizeMemberControls(controls: MemberControls | null | undefined): MemberControls | null {
  if (!controls) return null
  const normalized: MemberControls = { ...controls }
  if (Array.isArray(normalized.tiles)) {
    const seen = new Set<string>()
    const canonicalTiles: string[] = []
    for (const raw of normalized.tiles) {
      if (typeof raw === 'string') {
        const canonical = raw.trim().toLowerCase()
        if (
          canonical &&
          !seen.has(canonical) &&
          !isNeverHideableKey(canonical) &&
          HIDEABLE_TAB_KEY_SET.has(canonical)
        ) {
          seen.add(canonical)
          canonicalTiles.push(canonical)
        }
      }
    }
    normalized.tiles = canonicalTiles
  }
  return normalized
}

export function computeEffectiveControls(
  profileControls?: MemberControls | null,
  overrides?: MemberControls | null,
): MemberControls | null {
  if (!profileControls && !overrides) {
    return null
  }

  const effective: MemberControls = {}

  if (overrides?.funPack !== undefined && overrides.funPack !== 'INHERIT') {
    effective.funPack = overrides.funPack
  } else if (profileControls?.funPack !== undefined && profileControls.funPack !== 'INHERIT') {
    effective.funPack = profileControls.funPack
  } else {
    effective.funPack = 'INHERIT'
  }

  if (overrides?.chat !== undefined) {
    effective.chat = overrides.chat
  } else if (profileControls?.chat !== undefined) {
    effective.chat = profileControls.chat
  } else {
    effective.chat = 'FULL'
  }

  if (overrides?.ai !== undefined) {
    if (overrides.ai.mode === 'INHERIT') {
      effective.ai = profileControls?.ai ?? { mode: 'INHERIT' }
    } else {
      effective.ai = {
        mode: overrides.ai.mode,
        ...(overrides.ai.dailyCredits !== undefined ? { dailyCredits: overrides.ai.dailyCredits } : {}),
      }
    }
  } else if (profileControls?.ai !== undefined) {
    effective.ai = {
      mode: profileControls.ai.mode,
      ...(profileControls.ai.dailyCredits !== undefined ? { dailyCredits: profileControls.ai.dailyCredits } : {}),
    }
  } else {
    effective.ai = { mode: 'INHERIT' }
  }

  // 4. tiles: default null (drops unknown or never-hideable keys left in old data without failing)
  const selectedTiles = overrides?.tiles !== undefined ? overrides.tiles : profileControls?.tiles
  if (Array.isArray(selectedTiles)) {
    const valid: string[] = []
    const seen = new Set<string>()
    for (const raw of selectedTiles) {
      if (typeof raw === 'string') {
        const canonical = raw.trim().toLowerCase()
        if (
          canonical &&
          !seen.has(canonical) &&
          !isNeverHideableKey(canonical) &&
          HIDEABLE_TAB_KEY_SET.has(canonical)
        ) {
          seen.add(canonical)
          valid.push(canonical)
        }
      }
    }
    effective.tiles = valid
  } else {
    effective.tiles = null
  }

  return effective
}


export function resolveEffectiveControls(
  controls?: MemberControls | null,
): {
  funPack: FunPackControl
  chat: ChatControl
  ai: AiControl
  tiles: string[] | null
} {
  return {
    funPack: controls?.funPack ?? 'INHERIT',
    chat: controls?.chat ?? 'FULL',
    ai: controls?.ai ?? { mode: 'INHERIT' },
    tiles: controls?.tiles ?? null,
  }
}

/**
 * Detects whether a channel data object represents a Field Pass channel.
 * Matches firestore.rules: isFieldPassChannelData(channel).
 */
export function isFieldPassChannelData(channel?: Record<string, unknown> | null): boolean {
  if (!channel || typeof channel !== 'object') return false
  if (channel.identityType === 'FIELD_PASS') return true
  if ('fieldPassId' in channel && channel.fieldPassId !== undefined && channel.fieldPassId !== null) return true
  if (typeof channel.slug === 'string' && channel.slug.startsWith('field-pass-')) return true
  return false
}

/**
 * Detects whether a channel data object represents a Direct Message (DM) channel.
 * Matches firestore.rules: isDmChannelData(channel).
 * (private channel with exactly 2 roles, excluding Field Pass).
 */
export function isDmChannelData(channel?: Record<string, unknown> | null): boolean {
  if (!channel || typeof channel !== 'object') return false
  const type = channel.type ?? 'public'
  if (type !== 'private') return false
  const roles = channel.roles
  if (!roles || typeof roles !== 'object' || Array.isArray(roles)) return false
  if (Object.keys(roles).length !== 2) return false
  return !isFieldPassChannelData(channel)
}

/**
 * Evaluates whether a member with the given chat control mode can send a message
 * to the specified channel (TT3a.fix1 F4).
 * Matrix:
 * - FULL: any channel
 * - ANNOUNCEMENTS_ONLY: only if channel.announcement === true
 * - NO_DM: anything but a DM (using isDmChannelData)
 * - OFF: nothing (always false)
 */
export function canSendWithChatControl(
  mode: ChatControl | string | null | undefined,
  channel?: Record<string, unknown> | null,
): boolean {
  const normalizedMode = mode ?? 'FULL'
  if (normalizedMode === 'OFF') return false
  if (normalizedMode === 'ANNOUNCEMENTS_ONLY') {
    return Boolean(channel && typeof channel === 'object' && channel.announcement === true)
  }
  if (normalizedMode === 'NO_DM') {
    return !isDmChannelData(channel)
  }
  if (normalizedMode === 'FULL') return true
  return false
}

